import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { setCookie, deleteCookie } from '../lib/cookie'
import { isValidUuid } from '../lib/uuid'
import { apiClient } from '../api/client'
import { AGENT_COOKIE, REFERRER_COOKIE } from './agent-context'

const COOKIE_DAYS = 180

/** 全覆盖式写归因 cookie:有值写、null/undefined 删(2026-06-13 pending §11)。 */
function applyPromoCookies(referrer: string | null | undefined, agent: string | null | undefined) {
  if (referrer) setCookie(REFERRER_COOKIE, referrer, COOKIE_DAYS)
  else deleteCookie(REFERRER_COOKIE)
  if (agent) setCookie(AGENT_COOKIE, agent, COOKIE_DAYS)
  else deleteCookie(AGENT_COOKIE)
}

export function AgentPromoRoute() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  useEffect(() => {
    if (isValidUuid(userId)) {
      setCookie(AGENT_COOKIE, userId, COOKIE_DAYS)
    }
    navigate('/', { replace: true })
  }, [userId, navigate])
  return null
}

export function ReferrerPromoRoute() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { data } = useQuery({
    queryKey: ['promo', 'resolve', userId] as const,
    queryFn: () =>
      apiClient.get<{ referrer_for_cookie: string | null; agent_for_cookie: string | null }>(
        `/gapi/public/promo/resolve?ref=${userId!}`,
      ),
    enabled: isValidUuid(userId),
  })
  useEffect(() => {
    if (!data) return
    applyPromoCookies(data.referrer_for_cookie, data.agent_for_cookie)
    navigate('/', { replace: true })
  }, [data, navigate])
  useEffect(() => {
    if (!isValidUuid(userId)) navigate('/', { replace: true })
  }, [userId, navigate])
  return null
}

export type SharePackPreviewItem = {
  name: string
  type: string
  mime_type: string
  size_original: number | null
}

export type SharePackMeta = {
  pack_id: string
  owner_user_id: string
  owner_agent_id_for_cookie?: string | null
  owner_referrer_for_cookie?: string | null
  name: string
  description?: string
  access_mode: 'free' | 'paid'
  price_coin?: number
  owner_username_masked?: string
  owner_avatar_url?: string | null
  item_count?: number
  total_size?: number
  updated_at?: string
  preview_items?: SharePackPreviewItem[] | null
  subscriber_count?: number
  view_count?: number
}

export type SharePackRouteProps = {
  renderLoading?: () => React.ReactNode
  renderPage: (meta: SharePackMeta) => React.ReactNode
  renderError?: (err: unknown) => React.ReactNode
  /** 仅站外分享入口(/#/s/)置 true:覆盖式写归因 cookie。站内 /#/pack/ 不传(默认 false),不污染 cookie。 */
  setPromoCookies?: boolean
}

export function SharePackRoute({
  renderLoading,
  renderPage,
  renderError,
  setPromoCookies = false,
}: SharePackRouteProps) {
  const { packId } = useParams<{ packId: string }>()
  const { data, isLoading, error } = useQuery({
    queryKey: ['share', packId, 'meta'] as const,
    queryFn: () => apiClient.get<SharePackMeta>(`/gapi/public/share/${packId!}/meta`),
    enabled: isValidUuid(packId),
  })
  useEffect(() => {
    if (!setPromoCookies || !data) return
    applyPromoCookies(data.owner_referrer_for_cookie, data.owner_agent_id_for_cookie)
  }, [data, setPromoCookies])
  if (isLoading) return <>{renderLoading?.() ?? null}</>
  if (error) return <>{renderError?.(error) ?? null}</>
  if (!data) return null
  return <>{renderPage(data)}</>
}
